/**
 * An employee's details, for adding one and for correcting one (§18.3).
 *
 * ONE FORM FOR BOTH, so the fields cannot come to differ between adding and
 * correcting -- which is how an address typed on one screen goes missing on
 * the other.
 *
 * OWNER-OFFICER IS ASKED, NEVER PRE-SET, when adding. The database gives the
 * column no default (0031, invariant 10), and a radio already chosen is a
 * default by another name. When correcting, it starts as what is on file.
 *
 * OFFICER (0083, §B.16.4) IS ASKED THE SAME WAY. It decides the wages account:
 * a non-profit's officers are paid to Officer Compensation, for Form 990, and
 * an S corporation's other officers to Salaries and Wages. Owner-Officer, the
 * S corporation's more-than-2% shareholder, is asked only of an S corporation
 * (and of a business whose type was never recorded, as before); answering Yes
 * there answers Officer too, since the owner-officer always is one.
 *
 * A BUSINESS TAXED WITH ITS OWNER (0094), AND A PARTNERSHIP (0096), are asked
 * neither: an owner or a partner is never an employee, and neither business has
 * officers whose pay goes anywhere but Salaries and Wages. The form says so in
 * one sentence instead. Nothing refuses the owner here, since an employee names
 * no party, and a check that refused only an employee someone chose to link
 * would refuse nothing (PLAN_tax_treatments §4.5).
 *
 * THE SSN IS OFFERED ONLY WHEN ADDING. On an existing employee it is replaced
 * on its own, on purpose: a details form with an SSN field would send it blank
 * with every change of address, and a blank SSN REMOVES the number on file.
 */

import { useState } from 'react';
import type { CreateEmployeeBody, Employee } from '../api.js';
import { useBusiness } from '../auth/AuthProvider.js';

export interface EmployeeFormProps {
  /** The group's accessible name: "New Employee", "Edit Details". */
  label: string;
  initial?: Employee;
  withSsn: boolean;
  submitLabel: string;
  busy: boolean;
  onSubmit: (body: CreateEmployeeBody) => void;
  onCancel: () => void;
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Blank is absent: the server stores null for it, and so does this. */
function blankToNull(text: string): string | null {
  const trimmed = text.trim();
  return trimmed === '' ? null : trimmed;
}

export function EmployeeForm({
  label,
  initial,
  withSsn,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
}: EmployeeFormProps) {
  const [firstName, setFirstName] = useState(initial?.firstName ?? '');
  const [middleName, setMiddleName] = useState(initial?.middleName ?? '');
  const [lastName, setLastName] = useState(initial?.lastName ?? '');
  const [suffix, setSuffix] = useState(initial?.suffix ?? '');
  const [addressLine1, setAddressLine1] = useState(initial?.addressLine1 ?? '');
  const [addressLine2, setAddressLine2] = useState(initial?.addressLine2 ?? '');
  const [city, setCity] = useState(initial?.city ?? '');
  const [state, setState] = useState(initial?.state ?? '');
  const [postalCode, setPostalCode] = useState(initial?.postalCode ?? '');
  const [hireDate, setHireDate] = useState(initial?.hireDate ?? '');
  // Only an S corporation has an owner-officer; an unrecorded type is asked, as before 0078.
  const business = useBusiness();
  const asksOwnerOfficer = business == null || business.taxStatus == null || business.taxStatus === 's_corporation';
  const [ownerOfficer, setOwnerOfficer] = useState<boolean | null>(
    asksOwnerOfficer ? (initial?.isOwnerOfficer ?? null) : false,
  );
  const taxedWithOwner = business?.taxStatus === 'disregarded_entity';
  const partnership = business?.taxStatus === 'partnership';
  const ownersNotEmployees = taxedWithOwner || partnership;
  const [officer, setOfficer] = useState<boolean | null>(initial?.isOfficer ?? (ownersNotEmployees ? false : null));
  const [ssn, setSsn] = useState('');
  // The owner-officer is an officer, so Officer is answered by that Yes.
  const isOfficer = ownerOfficer === true ? true : officer;

  const ready =
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    DATE.test(hireDate) &&
    ownerOfficer !== null &&
    isOfficer !== null;

  function submit() {
    if (!ready || ownerOfficer === null || isOfficer === null) return;
    const body: CreateEmployeeBody = {
      firstName: firstName.trim(),
      middleName: blankToNull(middleName),
      lastName: lastName.trim(),
      suffix: blankToNull(suffix),
      addressLine1: blankToNull(addressLine1),
      addressLine2: blankToNull(addressLine2),
      city: blankToNull(city),
      state: blankToNull(state),
      postalCode: blankToNull(postalCode),
      hireDate,
      isOwnerOfficer: ownerOfficer,
      isOfficer,
    };
    if (withSsn) body.ssn = blankToNull(ssn);
    onSubmit(body);
  }

  return (
    <div className="new-account employee-form" role="group" aria-label={label}>
      <p className="muted">The legal name, as it appears on the social security card.</p>
      {/*
        A line at a time, as the fields are read off the card and an envelope:
        the name, the street, the city with its state and ZIP, then the dates
        payroll needs. One auto-filling grid had put Suffix beside Address.
      */}
      <div className="employee-fields">
        <div className="field-row">
          <label className="field">
            <span>First Name</span>
            <input value={firstName} onChange={(event) => setFirstName(event.target.value)} />
          </label>
          <label className="field">
            <span>Middle Name</span>
            <input value={middleName} onChange={(event) => setMiddleName(event.target.value)} />
          </label>
          <label className="field">
            <span>Last Name</span>
            <input value={lastName} onChange={(event) => setLastName(event.target.value)} />
          </label>
          <label className="field field-short">
            <span>Suffix</span>
            <input value={suffix} onChange={(event) => setSuffix(event.target.value)} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span>Address</span>
            <input value={addressLine1} onChange={(event) => setAddressLine1(event.target.value)} />
          </label>
          <label className="field">
            <span>Address Line 2</span>
            <input value={addressLine2} onChange={(event) => setAddressLine2(event.target.value)} />
          </label>
        </div>
        <div className="field-row">
          <label className="field">
            <span>City</span>
            <input value={city} onChange={(event) => setCity(event.target.value)} />
          </label>
          <label className="field field-short">
            <span>State</span>
            <input value={state} maxLength={2} onChange={(event) => setState(event.target.value)} />
          </label>
          <label className="field field-medium">
            <span>ZIP Code</span>
            <input value={postalCode} onChange={(event) => setPostalCode(event.target.value)} />
          </label>
        </div>
        <div className="field-row">
          <label className="field field-medium">
            <span>Hire Date</span>
            <input type="date" value={hireDate} onChange={(event) => setHireDate(event.target.value)} />
          </label>
          {withSsn && (
            <label className="field field-medium">
              <span>SSN</span>
              <input
                value={ssn}
                inputMode="numeric"
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setSsn(event.target.value)}
              />
            </label>
          )}
        </div>
      </div>

      {asksOwnerOfficer && (
        <fieldset className="choice">
          <legend>Owner-Officer</legend>
          <label>
            <input
              type="radio"
              name={`${label}-owner-officer`}
              checked={ownerOfficer === true}
              onChange={() => setOwnerOfficer(true)}
            />{' '}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name={`${label}-owner-officer`}
              checked={ownerOfficer === false}
              onChange={() => setOwnerOfficer(false)}
            />{' '}
            No
          </label>
        </fieldset>
      )}

      {taxedWithOwner && (
        <p className="muted">The owner is not an employee: pay yourself with a draw, never through payroll.</p>
      )}
      {partnership && (
        <p className="muted">A partner is not an employee: pay a partner with a draw or a guaranteed payment, never through payroll.</p>
      )}

      {/* Answered by Owner-Officer's Yes, and then shown as that answer rather than hidden. */}
      {!ownersNotEmployees && (
        <fieldset className="choice" disabled={ownerOfficer === true}>
          <legend>Officer</legend>
          <label>
            <input
              type="radio"
              name={`${label}-officer`}
              checked={isOfficer === true}
              onChange={() => setOfficer(true)}
            />{' '}
            Yes
          </label>
          <label>
            <input
              type="radio"
              name={`${label}-officer`}
              checked={isOfficer === false}
              onChange={() => setOfficer(false)}
            />{' '}
            No
          </label>
        </fieldset>
      )}

      <div className="row">
        <button type="button" disabled={!ready || busy} onClick={submit}>
          {submitLabel}
        </button>
        <button type="button" className="link" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}
